import { Renderer, Geometry, Program, Mesh } from "ogl"
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"
import { useRafLoop, useWindowSize } from "react-use"
import { twMerge } from "tailwind-merge"
import { ColorThief } from "@utils/color-thief"
import { generateThemePalette } from "@components/atoms/Themed"

export type GradientType = HTMLDivElement & {
  export: () => void
  useImg: (index: number) => void
  resize: () => void
}

export const Gradient = forwardRef<
  GradientType,
  {
    img?: string | string[]
    theme?: string
    className?: string
    align?: "left" | "center" | "right"
    aspect?: string
    dimensionUnits?: { width: "vw" | "vh" | "px"; height: "vw" | "vh" | "px" }
    outerWidth?: number
    outerHeight?: number
    bg?: { r: number; g: number; b: number }
    width?: number
    height?: number
    scale?: number
    direction?: "horizontal" | "vertical"
    showImage?: boolean
    blocking?: boolean
    radial?: number
    speed?: number
    paused?: boolean
    grain?: boolean
    cutEdges?: boolean
    fadeEdges?: boolean
    ripple1Size?: number
    ripple1Intensity?: number
    ripple2Size?: number
    ripple2Intensity?: number
    alpha?: number
    desaturated?: boolean
  }
>(
  (
    {
      img,
      theme,
      className,
      align = "center",
      aspect,
      dimensionUnits = { width: "vw", height: "vh" },
      outerWidth,
      outerHeight,
      bg,
      width,
      height,
      scale = 1,
      direction = "horizontal",
      showImage = true,
      blocking = true,
      radial = 3,
      speed = 1,
      paused = false,
      grain = true,
      cutEdges = true,
      fadeEdges = false,
      ripple1Size = 0.15,
      ripple1Intensity = 0.36,
      ripple2Size = 0.22,
      ripple2Intensity = 0.25,
      alpha = 1,
      desaturated = false,
    },
    usableRef,
  ) => {
    const outerRef = useRef<HTMLDivElement>(null)
    const ref = useRef<HTMLDivElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const imageRef = useRef<HTMLImageElement>(null)
    const spacerRef = useRef<HTMLDivElement>(null)
    const rendererRef = useRef<Renderer | null>(null)
    const geometryRef = useRef<Geometry | null>(null)
    const programRef = useRef<Program | null>(null)
    const meshRef = useRef<Mesh | null>(null)
    const { width: wW, height: wH } = useWindowSize()
    const imgsRef = useRef<HTMLImageElement[]>([])
    const colorsRef = useRef<{ r: number; b: number; g: number }[][]>([])
    const ctRef = useRef<ColorThief>(new ColorThief())
    const lastTime = useRef<number>(0)
    const pauseTime = useRef<number>(0)
    const colorLerp = useRef<{ r: number; rEase: number; g: number; gEase: number; b: number; bEase: number }[]>(
      [...Array(5)].map(() => ({ r: 0, rEase: 0, g: 0, gEase: 0, b: 0, bEase: 0 })),
    )
    const currentColorSet = useRef(0)
    const themeRef = useRef(theme ? generateThemePalette(theme) : null)

    useImperativeHandle(
      usableRef,
      () =>
        Object.assign(outerRef.current!, {
          export: exportCanvas,
          useImg: (index: number) => {
            currentColorSet.current = index
          },
          resize,
        }),
      [outerRef],
    )

    const exportCanvas = () => {
      if (!canvasRef.current) return
      // Convert the canvas to data
      const image = canvasRef.current.toDataURL()
      const link = document.createElement("a")
      link.download = `gradient${Date.now()}.png`
      link.href = image
      link.click()
    }

    useEffect(() => {
      if (!canvasRef.current || !ref.current) return

      rendererRef.current = new Renderer({
        width: ref.current.offsetWidth,
        height: ref.current.offsetHeight,
        canvas: canvasRef.current,
        alpha: true,
        preserveDrawingBuffer: true,
      })
      const gl = rendererRef.current.gl

      // Triangle that covers viewport, with UVs that still span 0 > 1 across viewport
      geometryRef.current = new Geometry(gl, {
        position: { size: 2, data: new Float32Array([-1, -1, 3, -1, -1, 3]) },
        uv: { size: 2, data: new Float32Array([0, 0, 2, 0, 0, 2]) },
      })

      programRef.current = new Program(gl, {
        vertex: /* glsl */ `
        attribute vec2 uv;
        attribute vec2 position;

        varying vec2 vUv;

        void main() {
          vUv = uv;
          gl_Position = vec4(position, 0, 1);
        }
      `,
        fragment: /* glsl */ `
        precision highp float;

        uniform float uTime;
        uniform vec2 uInnerSize;
        uniform vec3 uBg;
        uniform vec2 uResolution;
        uniform vec3 uColor1;
        uniform vec3 uColor2;
        uniform vec3 uColor3;
        uniform vec3 uColor4;
        uniform vec3 uColor5;
        uniform float uOpacity;
        uniform vec2 uImageSize;
        uniform vec2 uImagePos;
        uniform float uScale;
        uniform float uDirection;
        uniform float uSpacerSize;
        uniform float uSeed;
        uniform float uBlocker;
        uniform float uRadial; // 3.0
        uniform float uSpeed;
        uniform float uGrain;
        uniform float uCutEdges;
        uniform float uFadeEdges;
        uniform float uRipple1Size;
        uniform float uRipple1Intensity;
        uniform float uRipple2Size;
        uniform float uRipple2Intensity;
        uniform float uDesaturated;

        varying vec2 vUv;

        float rand(vec2 co){
          return fract(sin(dot(co.xy, vec2(12.9898,78.233))) * 43758.5453123);
        }

        float ripple(vec2 sourceCoord, vec2 centerCoord, float time, float multiplier) {
          float dist = distance(sourceCoord * multiplier, centerCoord * multiplier);
          return (sin(dist * 80.0 - time * 15.0) + 0.5) / 30.0;
        }

        float rip(float t, float offset, float dir) {
          float ratio = (uResolution.y / uResolution.x) * 5.5;
          return mix(vUv.x, vUv.y, uDirection) + sin(mix(vUv.y, vUv.x, uDirection) * uRadial) * ratio * dir * 0.025 + ripple(vUv, vec2(0.5), t * 0.15 * offset, uRipple1Size) * dir * uRipple1Intensity + ripple(vUv, vec2(0.5, 0.0), t * offset * 0.35, uRipple2Size) * dir * sin(vUv.y) * uRipple2Intensity;
        }

        float ringtime(float t, float offset) {
          return t * offset;
        }

        void main() {
          vec3 color = uBg;

          float ratio = uScale * (uResolution.y / uResolution.x) * 5.5 * (uInnerSize.y / uResolution.y);
          float rings = (mix(uResolution.x, uResolution.x, uDirection) / 18.0) * ratio;
          float t = (uTime * uSpeed * 1.2);
          float PI = 3.14159265359;
          float spacingDirection = mix((uResolution.x / rings), (uResolution.y / rings), uDirection);
          float spacing = ((PI * spacingDirection) + uSeed) * ratio;
          float dRings = mix(uResolution.x / rings, uResolution.y / rings, uDirection);
          float centerMixer = mix(smoothstep(uImagePos.x - 0.1, uImagePos.x + 0.1, vUv.x), smoothstep(uImagePos.y - 0.1, uImagePos.y + 0.1, vUv.y), uDirection);

          vec3 color1 = color;

          color1 = mix(uColor1, uColor2, clamp(sin((rip(t, 1.0, 1.0) * dRings) + ringtime(t, 1.0) + spacing * 0.5), 0.0, 1.0));
          color1 = mix(color1, uColor3, clamp(sin((rip(t, 0.5, 1.0) * dRings) + ringtime(t, 0.5) + spacing * 1.0), 0.0, 1.0));
          color1 = mix(color1, uColor4, clamp(sin((rip(t, 0.75, 1.0) * dRings) + ringtime(t, 0.75) + spacing * 1.5), 0.0, 1.0));
          color1 = mix(color1, uColor5, clamp(sin((rip(t, 0.25, 1.0) * dRings) + ringtime(t, 0.25) + spacing * 2.0), 0.0, 1.0));

          vec3 color2 = color;
          
          color2 = mix(uColor1, uColor2, clamp(sin((rip(t, 1.0, -1.0) * dRings) - ringtime(t, 1.0) + spacing * 0.5), 0.0, 1.0));
          color2 = mix(color2, uColor3, clamp(sin((rip(t, 0.5, -1.0) * dRings) - ringtime(t, 0.5) + spacing * 1.0), 0.0, 1.0));
          color2 = mix(color2, uColor4, clamp(sin((rip(t, 0.75, -1.0) * dRings) - ringtime(t, 0.75) + spacing * 1.5), 0.0, 1.0));
          color2 = mix(color2, uColor5, clamp(sin((rip(t, 0.25, -1.0) * dRings) - ringtime(t, 0.25) + spacing * 2.0), 0.0, 1.0));
          
          color = mix(color1, color2, centerMixer); //step(uImagePos.x, vUv.x));

          color = mix(color, vec3(dot(color, vec3(0.2))), uDesaturated);

          float noisey = rand(vUv + fract(uTime));
          color += noisey * 0.07 * uGrain;

          // reduce banding
          vec3 magic = vec3(0.06711056, 0.00583715, 52.9829189);
          color += fract(magic.z * fract(dot(gl_FragCoord.xy, magic.xy))) / 255.0;

          float blockerR = max(1.0 - uBlocker, step(uImagePos.x + (uImageSize.x / 2.0) + uSpacerSize, vUv.x));
          float blockerL = max(1.0 - uBlocker, 1.0 - step(uImagePos.x - (uImageSize.x / 2.0) - uSpacerSize, vUv.x));

          float rX = (uInnerSize.x / uResolution.x) * 0.5;
          float rY = (uInnerSize.y / uResolution.y) * 0.5;
          float outerX = smoothstep(uImagePos.x - rX - (rX * 1.5), uImagePos.x - rX, vUv.x) * smoothstep(uImagePos.x + rX + (rX * 1.5), uImagePos.x + rX, vUv.x);
          float outerY = smoothstep(uImagePos.y - rY - (rY * 1.5), uImagePos.y - rY, vUv.y) * smoothstep(uImagePos.y + rY + (rY * 1.5), uImagePos.y + rY, vUv.y);
          float cutX = step(0.5 - rX, vUv.x) * step(vUv.x, 0.5 + rX);
          float cutY = step(0.5 - rY, vUv.y) * step(vUv.y, 0.5 + rY);
          
          float alphaBlock = step(uCutEdges, mix(blockerL, blockerR, step(uImagePos.x, vUv.x)));

          gl_FragColor.rgb = mix(uBg, mix(uBg, color, alphaBlock) * mix(1.0, outerX * outerY, uFadeEdges), step(uCutEdges, cutX * cutY));
          gl_FragColor.a = 1.0 * uOpacity;
        }
      `,
        uniforms: {
          uTime: { value: 0 },
          uResolution: { value: [0, 0] },
          uInnerSize: { value: [0, 0] },
          uBg: { value: [0, 0, 0] },
          uImageSize: { value: [0, 0] },
          uImagePos: { value: [0, 0] },
          uScale: { value: 1 },
          uDirection: { value: 0 },
          uColor1: { value: [0, 0, 0] },
          uColor2: { value: [0, 0, 0] },
          uColor3: { value: [0, 0, 0] },
          uColor4: { value: [0, 0, 0] },
          uColor5: { value: [0, 0, 0] },
          uOpacity: { value: alpha },
          uSpacerSize: { value: 0 },
          uSeed: { value: Math.random() * 100 },
          uBlocker: { value: blocking ? 1.0 : 0.0 },
          uRadial: { value: radial },
          uSpeed: { value: 1.0 },
          uGrain: { value: grain ? 1.0 : 0.0 },
          uCutEdges: { value: cutEdges ? 1.0 : 0.0 },
          uFadeEdges: { value: fadeEdges ? 1.0 : 0.0 },
          uRipple1Size: { value: 0.15 },
          uRipple1Intensity: { value: 0.36 },
          uRipple2Size: { value: 0.22 },
          uRipple2Intensity: { value: 0.25 },
          uDesaturated: { value: desaturated ? 1.0 : 0.0 },
        },
      })

      meshRef.current = new Mesh(gl, {
        geometry: geometryRef.current,
        program: programRef.current,
      })

      return () => {
        rendererRef.current = null
        geometryRef.current?.remove()
        geometryRef.current = null
        programRef.current?.remove()
        programRef.current = null
        meshRef.current = null
      }
    }, [])

    useRafLoop((elapsedTime) => {
      if (!rendererRef.current || !programRef.current || !meshRef.current) return

      const delta = elapsedTime - lastTime.current

      const u = programRef.current.uniforms

      if (paused) {
        pauseTime.current += delta
      }

      u.uTime.value = (elapsedTime - pauseTime.current) / 1000
      u.uResolution.value = [rendererRef.current.width, rendererRef.current.height]

      u.uInnerSize.value = [ref.current?.offsetWidth || 0, ref.current?.offsetHeight || 0]

      if (u.uSpacerSize.value > 0 && u.uImageSize.value[0] > 0 && u.uImageSize.value[1] > 0) {
        u.uOpacity.value += (alpha - u.uOpacity.value) * 0.025
      }

      u.uBlocker.value = blocking ? 1.0 : 0.0
      u.uRadial.value = radial
      u.uSpeed.value = speed
      u.uGrain.value = grain ? 1.0 : 0.0
      u.uScale.value = scale
      u.uDirection.value = direction === "horizontal" ? 0.0 : 1.0
      u.uCutEdges.value = cutEdges ? 1.0 : 0.0
      u.uFadeEdges.value = fadeEdges ? 1.0 : 0.0
      u.uRipple1Size.value = ripple1Size
      u.uRipple1Intensity.value = ripple1Intensity
      u.uRipple2Size.value = ripple2Size
      u.uRipple2Intensity.value = ripple2Intensity
      u.uDesaturated.value += ((desaturated ? 1.0 : 0.0) - u.uDesaturated.value) * 0.1

      u.uBg.value = (bg && [bg.r / 255, bg.g / 255, bg.b / 255]) || [0, 0, 0]

      if (colorsRef.current.length > 0) {
        for (let x = 0; x < 5; x++) {
          if (colorsRef.current[currentColorSet.current]?.[x]) {
            colorLerp.current[x].r += (colorsRef.current[currentColorSet.current][x].r - colorLerp.current[x].r) * 0.1
            colorLerp.current[x].g += (colorsRef.current[currentColorSet.current][x].g - colorLerp.current[x].g) * 0.1
            colorLerp.current[x].b += (colorsRef.current[currentColorSet.current][x].b - colorLerp.current[x].b) * 0.1
            u[`uColor${x + 1}`].value = [colorLerp.current[x].r, colorLerp.current[x].g, colorLerp.current[x].b]
          }
        }
      }

      setImageSize()
      setSpacerSize()

      rendererRef.current.render({ scene: meshRef.current })

      lastTime.current = elapsedTime
    })

    const setImageSize = () => {
      if (!rendererRef.current || !programRef.current || !imageRef.current) return
      programRef.current.uniforms.uImageSize.value = [
        imageRef.current.offsetWidth / rendererRef.current.width,
        imageRef.current.offsetHeight / rendererRef.current.height,
      ]

      const posX = (imageRef.current.offsetLeft + imageRef.current.offsetWidth / 2) / rendererRef.current.width
      const posY = (imageRef.current.offsetTop + imageRef.current.offsetHeight / 2) / rendererRef.current.height
      programRef.current.uniforms.uImagePos.value = [posX, posY]
    }

    const setSpacerSize = () => {
      if (!spacerRef.current || !programRef.current || !rendererRef.current) return
      programRef.current.uniforms.uSpacerSize.value = spacerRef.current.offsetWidth / rendererRef.current.width
    }

    const resize = () => {
      if (rendererRef.current && canvasRef.current && outerRef.current) {
        rendererRef.current.setSize(outerRef.current?.offsetWidth, outerRef.current?.offsetHeight)
      }
    }

    useEffect(() => {
      resize()
    }, [width, height, wW, wH, outerWidth, outerHeight])

    const imageLoaded = async (index: number) => {
      if (!imgsRef.current || !rendererRef.current || !programRef.current || !meshRef.current) return

      const color = await ctRef.current.getPalette(imgsRef.current[index], 5)
      colorsRef.current[index] = []
      const c = colorsRef.current[index]

      for (let x = 0; x < 5; x++) {
        c[x] = { r: 0, g: 0, b: 0 }
        c[x].r = color[x][0] / 255
        c[x].g = color[x][1] / 255
        c[x].b = color[x][2] / 255
      }

      setImageSize()
    }

    useEffect(() => {
      if (!img) return

      if (Array.isArray(img)) {
        img.forEach((im, i) => {
          imgsRef.current[i] = new Image()
          imgsRef.current[i].crossOrigin = "anonymous"
          imgsRef.current[i].onload = () => {
            imageLoaded(i)
          }
          imgsRef.current[i].src = im
        })
      } else {
        imgsRef.current[0] = new Image()
        imgsRef.current[0].crossOrigin = "anonymous"
        imgsRef.current[0].onload = () => {
          imageLoaded(0)
        }
        imgsRef.current[0].src = img
      }
    }, [img])

    useEffect(() => {
      if (!theme) return
      themeRef.current = generateThemePalette(theme)

      if (themeRef.current) {
        colorsRef.current[currentColorSet.current] = []
        const c = colorsRef.current[currentColorSet.current]
        for (let x = 0; x < 5; x++) {
          c[x] = { r: 0, g: 0, b: 0 }
          c[x].r = themeRef.current[x][0] / 255
          c[x].g = themeRef.current[x][1] / 255
          c[x].b = themeRef.current[x][2] / 255
        }
      }
    }, [theme])

    const outerStyle: React.CSSProperties = {}
    if (outerWidth) outerStyle.width = `${outerWidth}${dimensionUnits.width}`
    if (outerHeight) outerStyle.height = `${outerHeight}${dimensionUnits.height}`

    const refStyle: React.CSSProperties = {}
    if (width) refStyle.width = `${width}${dimensionUnits.width}`
    if (height) refStyle.height = `${height}${dimensionUnits.height}`

    return (
      <div
        ref={outerRef}
        className={twMerge("flex justify-center items-center relative w-full", className)}
        style={outerStyle}
      >
        <canvas ref={canvasRef} className="w-full h-full absolute left-0 top-0" />
        <div
          ref={ref}
          className={twMerge(
            "w-full h-full flex items-center",
            align === "left" && "justify-start",
            align === "center" && "justify-center",
            align === "right" && "justify-end",
          )}
          style={refStyle}
        >
          <img
            ref={imageRef}
            src={Array.isArray(img) ? img[0] : img}
            className={twMerge(
              "h-full w-auto relative",
              aspect && "object-cover",
              showImage ? "opacity-100" : "opacity-0",
            )}
            style={aspect ? { aspectRatio: showImage ? aspect : "0.001/1" } : {}}
          />
          <div ref={spacerRef} className="w-4 absolute left-0 top-0 pointer-events-none opacity-0" />
        </div>
      </div>
    )
  },
)

Gradient.displayName = "Gradient"
